// ShareProfile Component
// Modal for sharing player profile

import React, { useState } from 'react';
import { 
  FaTimes, 
  FaCopy, 
  FaFacebook, 
  FaTwitter, 
  FaLinkedin, 
  FaWhatsapp,
  FaEnvelope,
  FaCheck 
} from 'react-icons/fa';
import styles from './ShareProfile.module.css';
import { useNotification } from '../../context/NotificationContext';

const ShareProfile = ({ profileUrl, profileName, onClose }) => {
  const { showSuccess } = useNotification();
  const [copied, setCopied] = useState(false);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle copy link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    showSuccess('Profile link copied to clipboard!');
    
    // Reset copied state after 2 seconds
    setTimeout(() => setCopied(false), 2000);
  };

  // Share handlers
  const shareHandlers = {
    facebook: () => {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`,
        '_blank',
        'width=600,height=400'
      );
    },
    twitter: () => {
      const text = `Check out ${profileName}'s sports profile!`;
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(profileUrl)}`,
        '_blank',
        'width=600,height=400'
      );
    },
    linkedin: () => {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`,
        '_blank',
        'width=600,height=400'
      );
    },
    whatsapp: () => {
      const text = `Check out ${profileName}'s sports profile: ${profileUrl}`;
      window.open(
        `https://wa.me/?text=${encodeURIComponent(text)}`,
        '_blank'
      );
    },
    email: () => {
      const subject = `${profileName}'s Sports Profile`;
      const body = `I wanted to share ${profileName}'s sports profile with you:\n\n${profileUrl}`;
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h3 id="share-modal-title">Share Profile</h3>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close share modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* URL Input */}
          <div className={styles.urlContainer}>
            <input
              type="text"
              value={profileUrl}
              readOnly
              className={styles.urlInput}
            />
            <button
              className={`${styles.copyButton} ${copied ? styles.copied : ''}`}
              onClick={handleCopyLink}
            >
              {copied ? <FaCheck /> : <FaCopy />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          {/* Share Options */}
          <div className={styles.shareOptions}>
            <h4>Share via</h4>
            <div className={styles.shareButtons}>
              <button
                className={`${styles.shareButton} ${styles.facebook}`}
                onClick={shareHandlers.facebook}
                aria-label="Share on Facebook"
              >
                <FaFacebook />
                <span>Facebook</span>
              </button>

              <button
                className={`${styles.shareButton} ${styles.twitter}`}
                onClick={shareHandlers.twitter}
                aria-label="Share on Twitter"
              >
                <FaTwitter />
                <span>Twitter</span>
              </button>

              <button
                className={`${styles.shareButton} ${styles.linkedin}`}
                onClick={shareHandlers.linkedin}
                aria-label="Share on LinkedIn"
              >
                <FaLinkedin />
                <span>LinkedIn</span>
              </button>

              <button
                className={`${styles.shareButton} ${styles.whatsapp}`}
                onClick={shareHandlers.whatsapp}
                aria-label="Share on WhatsApp"
              >
                <FaWhatsapp />
                <span>WhatsApp</span>
              </button>

              <button
                className={`${styles.shareButton} ${styles.email}`}
                onClick={shareHandlers.email}
                aria-label="Share via Email"
              >
                <FaEnvelope />
                <span>Email</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProfile;
